// "Frequently bought together": keeps the total and button label in step with the ticked
// products, follows variant changes of the main product, and adds the whole combination
// to the cart in one request, refreshing the cart drawer the way Dawn's product form does.
if (!customElements.get('product-combine')) {
  customElements.define(
    'product-combine',
    class ProductCombine extends HTMLElement {
      connectedCallback() {
        this.base = this.querySelector('[data-base]');
        this.addOns = [...this.querySelectorAll('input[data-variant-id]:not([data-base])')];
        this.button = this.querySelector('[data-add]');
        this.total = this.querySelector('[data-total]');
        this.basePrice = this.querySelector('[data-base-price]');
        this.status = this.querySelector('[data-status]');
        this.errorMessage = this.querySelector('[data-error-message]');

        this.addOns.forEach((input) => input.addEventListener('change', () => this.update()));
        this.button.addEventListener('click', () => this.addToCart());

        if (typeof subscribe === 'function') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
        }
        this.update();
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      get selected() {
        return [this.base, ...this.addOns.filter((input) => input.checked)];
      }

      update() {
        const items = this.selected;
        const cents = items.reduce((sum, input) => sum + Number(input.dataset.price), 0);
        this.total.textContent = this.formatMoney(cents);
        this.button.textContent =
          items.length === 1 ? this.dataset.labelOne : this.dataset.labelMany.replace('__N__', items.length);
        this.addOns.forEach((input) => {
          this.querySelector(`[data-tile="${input.dataset.variantId}"]`)?.classList.toggle('is-off', !input.checked);
        });
      }

      onVariantChange(event) {
        const { sectionId, variant } = event.data || {};
        if (!variant || sectionId !== this.dataset.sectionId) return;
        this.base.dataset.variantId = variant.id;
        this.base.dataset.price = variant.price;
        this.basePrice.textContent = this.formatMoney(variant.price);
        this.baseUnavailable = !variant.available;
        this.button.disabled = this.baseUnavailable;
        this.update();
      }

      async addToCart() {
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const items = this.selected.map((input) => ({ id: Number(input.dataset.variantId), quantity: 1 }));
        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(this.button);
        }

        this.setBusy(true);
        this.errorMessage.hidden = true;

        try {
          const response = await fetch(`${routes.cart_add_url}`, {
            ...fetchConfig('json'),
            body: JSON.stringify(body),
          }).then((res) => res.json());

          if (response.status) throw new Error(response.description || response.message || this.dataset.error);

          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-combine', cartData: response });
          }
          this.announce(this.dataset.added);

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }
          const first = response.items?.[0];
          cart.renderContents({ ...response, id: first?.id, key: first?.key });
          cart.classList.remove('is-empty');
        } catch (error) {
          this.errorMessage.textContent = error.message || this.dataset.error;
          this.errorMessage.hidden = false;
          this.announce(this.errorMessage.textContent);
        } finally {
          this.setBusy(false);
        }
      }

      setBusy(busy) {
        this.button.disabled = busy || Boolean(this.baseUnavailable);
        this.button.setAttribute('aria-busy', String(busy));
        this.addOns.forEach((input) => (input.disabled = busy));
      }

      announce(text) {
        this.status.textContent = '';
        requestAnimationFrame(() => (this.status.textContent = text));
      }

      // Shopify money formats, so the total matches the prices rendered by Liquid.
      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const type = (format.match(placeholder) || [])[1] || 'amount';
        const value = Number(cents) / 100;
        const group = (decimals, thousands, decimal) => {
          const [whole, fraction] = value.toFixed(decimals).split('.');
          return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
        };
        const formatted = {
          amount: () => group(2, ',', '.'),
          amount_no_decimals: () => group(0, ',', '.'),
          amount_with_comma_separator: () => group(2, '.', ','),
          amount_no_decimals_with_comma_separator: () => group(0, '.', ','),
          amount_with_space_separator: () => group(2, ' ', ','),
          amount_with_period_and_space_separator: () => group(2, ' ', '.'),
          amount_with_apostrophe_separator: () => group(2, "'", '.'),
        }[type];
        return format.replace(placeholder, formatted ? formatted() : group(2, ',', '.'));
      }
    }
  );
}
